import { useState } from 'react'
import { X } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { EmailAvatar } from '@/components/AccessAvatars'

const ACCESS_COLLAPSE_LIMIT = 8

export type AccessEditorProps = {
  value: string[]
  onChange: (next: string[]) => void
  /** Distinguishes the input from any other on the same screen. */
  id: string
  label?: string
  placeholder?: string
  disabled?: boolean
  /** The viewer's email; its chip is marked "(you)". */
  selfEmail?: string
}

/**
 * Chip editor for a list of emails: type an address, press Enter or comma to
 * commit it, backspace on an empty input to take the last one back. Collapses
 * past eight chips so a widely-shared record does not grow its container.
 *
 * Extracted from DocumentDetailsModal so documents and duties share one
 * sharing affordance rather than drifting into two.
 */
export function AccessEditor({
  value,
  onChange,
  id,
  label = 'Add people',
  placeholder = 'Add email, press Enter',
  disabled = false,
  selfEmail,
}: AccessEditorProps) {
  const [emailInput, setEmailInput] = useState('')
  const [expanded, setExpanded] = useState(false)

  const overLimit = value.length > ACCESS_COLLAPSE_LIMIT
  const visible =
    expanded || !overLimit ? value : value.slice(0, ACCESS_COLLAPSE_LIMIT)

  // Accepts one or many: splits on commas/semicolons/whitespace so a pasted
  // `a@x.com, b@y.com` becomes separate chips on Enter/comma/blur.
  const commitEmail = () => {
    const candidates = emailInput
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (candidates.length) {
      const next = [...value]
      for (const email of candidates) {
        if (!next.includes(email)) next.push(email)
      }
      onChange(next)
    }
    setEmailInput('')
  }

  const removeEmail = (email: string) =>
    onChange(value.filter((e) => e !== email))

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {value.length > 0 && (
          <span className="ml-1 font-normal text-muted-foreground">
            ({value.length})
          </span>
        )}
      </Label>
      <Input
        id={id}
        value={emailInput}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setEmailInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            commitEmail()
          } else if (e.key === 'Backspace' && !emailInput && value.length) {
            const last = value[value.length - 1]
            if (last) removeEmail(last)
          }
        }}
        onBlur={commitEmail}
      />
      {value.length > 0 && (
        // Collapsed: first N chips + "+M more". Expanded: all, height-capped
        // with scroll so 50+ people do not grow the container.
        <div
          className={cn(
            'mt-1 flex flex-wrap gap-1.5',
            expanded && 'max-h-40 overflow-y-auto'
          )}>
          {visible.map((email) => (
            <span
              key={email}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-muted/40 py-0.5 pl-0.5 pr-2 text-sm">
              <EmailAvatar email={email} className="size-5 border-0" />
              <span className="truncate">{email}</span>
              {email.toLowerCase() === selfEmail?.toLowerCase() && (
                <span className="shrink-0 text-muted-foreground">(you)</span>
              )}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                disabled={disabled}
                onClick={() => removeEmail(email)}
                className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground">
                <X className="size-3.5" />
              </button>
            </span>
          ))}
          {overLimit && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center rounded-full border border-dashed px-2 py-0.5 text-sm text-muted-foreground hover:bg-black/5 hover:text-foreground">
              {expanded
                ? 'Show less'
                : `+${value.length - ACCESS_COLLAPSE_LIMIT} more`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
